import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import Swal from "sweetalert2";

// Cell Components
const SerialCell = ({ value }) => (
  <MDTypography variant="caption" color="text" fontWeight="bold">
    {value}
  </MDTypography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const DepartmentCell = ({ value, row }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="button" fontWeight="medium">
      {value}
    </MDTypography>
    <MDTypography variant="caption" color="text">
      Created: {new Date(row.original.createdAt).toLocaleDateString()}
    </MDTypography>
  </MDBox>
);
DepartmentCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
};

const StatusCell = ({ value }) => (
  <MDBox ml={-1}>
    <MDBadge
      badgeContent={value}
      color={value === "active" ? "success" : "error"}
      variant="gradient"
      size="sm"
    />
  </MDBox>
);
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <MDBox display="flex" justifyContent="center" gap={1}>
    <MDButton variant="gradient" color="info" size="small" onClick={() => handleEdit(row.original)}>
      <Icon>edit</Icon>&nbsp;Edit
    </MDButton>
    <MDButton
      variant="gradient"
      color="error"
      size="small"
      onClick={() => handleDelete(row.original)}
    >
      <Icon>delete</Icon>&nbsp;Delete
    </MDButton>
  </MDBox>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function usePaymentData() {
  const { user, church } = useAuth();
  const searchTimeoutRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  const [paymentData, setPaymentData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      {
        Header: "Member ID",
        accessor: "memberId",
        align: "left",
        width: "25%",
      },
      {
        Header: "Member",
        accessor: "memberName",
        align: "left",
        width: "25%",
        Cell: DepartmentCell,
      },
      { Header: "Type", accessor: "type", align: "center", width: "15%", Cell: SerialCell },
      { Header: "Amount", accessor: "amount", align: "center", width: "15%", Cell: SerialCell },
      { Header: "Method", accessor: "method", align: "center", width: "15%", Cell: SerialCell },
      {
        Header: "Reference",
        accessor: "reference",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      {
        Header: "Payment Date",
        accessor: "paymentDate",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "15%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  // Check for payment success on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentRef = params.get("reference");
    const paymentStatus = params.get("payment");

    if (paymentStatus === "success" && paymentRef) {
      verifyPayment(paymentRef);
      // Clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const verifyPayment = async (reference) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `https://api.ofi-cia.com/api/payments/verify?reference=${reference}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.status) {
        Swal.fire({
          title: "Payment Successful!",
          text: `Thank you for your payment of GHS ${data.payment.amount}`,
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchPayments();
      } else {
        Swal.fire({
          title: "Verification Failed",
          text: data.message || "Could not verify payment",
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "An error occurred while verifying payment",
        icon: "error",
      });
    }
  };

  const searchMembers = useCallback(
    async (query) => {
      if (!church?.id || !query.trim()) {
        setFilteredMembers([]);
        return;
      }

      setIsLoadingMembers(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;
        const response = await fetch(
          `https://api.ofi-cia.com/api/members/${church.id}/search?query=${encodeURIComponent(
            query
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        setFilteredMembers(result.data || []);
      } catch (err) {
        console.error("Search failed:", err);
        setFilteredMembers([]);
        Swal.fire({
          title: "Search Error",
          text: err.message,
          icon: "error",
        });
      } finally {
        setIsLoadingMembers(false);
      }
    },
    [church?.id]
  );

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredMembers([]);
      setShowMemberDropdown(false);
      return;
    }

    const timer = setTimeout(() => {
      searchMembers(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchMembers]);

  const loadAllMembers = useCallback(async () => {
    if (!church?.id) return;

    setIsLoadingMembers(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`https://api.ofi-cia.com/api/members/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch members");
      const data = await response.json();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load members:", err);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [church?.id]);

  const handleMemberSelect = (member) => {
    setSelectedPayment((prev) => ({
      ...prev,
      member_id: member.id,
      member_name: `${member.first_name} ${member.surname}`,
      member: member,
    }));
    setSearchTerm(`${member.first_name} ${member.surname}`);
    setShowMemberDropdown(false);
  };

  const handleEdit = useCallback((payment) => {
    setSelectedPayment({
      ...payment,
      paymentDate: payment.paymentDate || new Date().toISOString().split("T")[0],
    });
    setSearchTerm(payment.memberName || "");
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((payment) => {
    setSelectedPayment(payment);
    setDeleteModalOpen(true);
  }, []);

  const handleEditSubmit = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedPayment.id
        ? `https://api.ofi-cia.com/api/payments/${selectedPayment.id}`
        : "https://api.ofi-cia.com/api/payments";
      const method = selectedPayment.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: selectedPayment.member_id,
          type: selectedPayment.type || "welfare",
          amount: selectedPayment.amount,
          method: selectedPayment.method || "cash",
          reference: selectedPayment.reference || "",
          payment_date: selectedPayment.paymentDate,
          status: selectedPayment.status === "active",
          church_id: church.id,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: `Payment successfully ${selectedPayment.id ? "updated" : "created"}`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchPayments();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Submit failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [selectedPayment, church?.id]);

  const handlePaystackSubmit = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Please login to continue");

      const response = await fetch("https://api.ofi-cia.com/api/payments/paystack/initialize", {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: selectedPayment.member_id,
          email: selectedPayment.email,
          amount: selectedPayment.amount,
          type: selectedPayment.type || "welfare",
          church_id: church.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Payment initialization failed");
      }

      if (data.data?.authorization_url) {
        window.location.href = data.data.authorization_url;
      } else {
        throw new Error("No payment URL received");
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
      });
    }
  }, [selectedPayment, church?.id]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`https://api.ofi-cia.com/api/payments/${selectedPayment.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: "Payment deleted successfully",
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchPayments();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [selectedPayment]);

  const handleAddPayment = useCallback(() => {
    setSelectedPayment({
      id: null,
      member_id: "",
      type: "welfare",
      amount: "",
      method: "manual",
      reference: "",
      paymentDate: new Date().toISOString().split("T")[0],
      status: "active",
    });
    setSearchTerm("");
    setFilteredMembers([]);
    setEditModalOpen(true);
  }, []);

  const handlePrint = useCallback(() => window.print(), []);

  const fetchPayments = useCallback(async () => {
    try {
      setPaymentData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(
        `https://api.ofi-cia.com/api/payments/members/${church.id}?type=welfare`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const payments = result.data || result.payments || result;

      setPaymentData((prev) => ({
        ...prev,
        rows: payments.map((pay, index) => ({
          id: pay.id,
          serial: index + 1,
          memberId: pay.memberID || "N/A",
          memberName: `${pay.member?.first_name || ""} ${pay.member?.surname || ""}`,
          type: pay.type,
          amount: pay.amount,
          method: pay.method,
          reference: pay.reference,
          paymentDate: new Date(pay.payment_date).toLocaleDateString(),
          status: pay.status ? "active" : "inactive",
          createdAt: pay.created_at,
          originalData: pay,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setPaymentData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [user, church]);

  useEffect(() => {
    if (church?.id) {
      fetchPayments();
      loadAllMembers();
    }
  }, [church?.id, fetchPayments, loadAllMembers]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .swal2-container {
        z-index: 9999 !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return {
    ...paymentData,
    members,
    isLoadingMembers,
    filteredMembers,
    handleMemberSelect,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayment,
    setSelectedPayment,
    handleEditSubmit,
    handlePaystackSubmit,
    handleDeleteConfirm,
    handleAddPayment,
    searchTerm,
    setSearchTerm,
    loadAllMembers,
    showMemberDropdown,
    setShowMemberDropdown,
  };
}
